import React, { Component } from 'react';
import { AuthRoute as Route } from 'react-router-auth';
import { withRouter, Route as AnonymousRoute } from 'react-router-dom';
import { connect } from 'react-redux';
import asyncComponent from '../../components/AsyncComponent';
import Nav from '../Nav';
import Navigation from '../../components/MobileNavigation';
import { setCurrentUser } from '../Login/actions';

const AsyncDashboard = asyncComponent(() => import('../Dashboard'));
const AsyncLogin = asyncComponent(() => import('../Login'));
const AsyncOrders = asyncComponent(() => import('../Orders'));
const AsyncWaitTime = asyncComponent(() => import('../WaitTime'));
const AsyncAuthCallback = asyncComponent(() => import('../AuthCallback'));
const AsyncMenu = asyncComponent(() => import('../Menu'));
const AsyncCreateMenuCategory = asyncComponent(() => import('../CreateMenuCategory'));
const AsyncEditCategory = asyncComponent(() => import('../EditMenuCategory'));
const AsyncSingleMenuItem = asyncComponent(() => import('../SingleMenuItem'));
const AsyncCreateMenuItem = asyncComponent(() => import('../CreateMenuItem'));
const AsyncCreateModifierGroup = asyncComponent(() => import('../CreateModifierGroup'));
const AsyncCreateModifier = asyncComponent(() => import('../CreateModifier'));
const AsyncEditMenuItem = asyncComponent(() => import('../EditMenuItem'));
const AsyncEditModifierGroup = asyncComponent(() => import('../EditModifierGroup'));
const AsyncEditModifier = asyncComponent(() => import('../EditModifier'));
const AsyncPartners = asyncComponent(() => import('../Partners'));
const AsyncCreatePartner = asyncComponent(() => import('../CreatePartner'));
const AsyncEditPartner = asyncComponent(() => import('../EditPartner'));
const AsyncResetPassword = asyncComponent(() => import('../ResetPassword'));
const AsyncUpdateSettings = asyncComponent(() => import('../Settings'));
const AsyncUpdateAvailability = asyncComponent(() => import('../UpdateAvailability'));
const AsyncGuide = asyncComponent(() => import('../Guide'));
const AsyncBulkUpload = asyncComponent(() => import('../BulkUpload'));
const AsyncBulkUploadHistory = asyncComponent(() => import('../BulkUploadHistory'));
const AsyncBulkUploadDetail = asyncComponent(() => import('../BulkUploadDetail'));
const AsyncRatings = asyncComponent(() => import('../Ratings'));

const lazyRoutes = [
  AsyncDashboard, AsyncLogin, AsyncOrders, AsyncWaitTime, AsyncAuthCallback,
  AsyncMenu, AsyncCreateMenuCategory, AsyncEditCategory, AsyncSingleMenuItem,
  AsyncCreateMenuItem, AsyncCreateModifierGroup, AsyncCreateModifier,
  AsyncEditMenuItem, AsyncEditModifierGroup, AsyncEditModifier, AsyncPartners,
  AsyncCreatePartner, AsyncEditPartner, AsyncResetPassword, AsyncUpdateSettings,
  AsyncUpdateAvailability, AsyncGuide, AsyncBulkUpload,
  AsyncBulkUploadHistory, AsyncBulkUploadDetail, AsyncRatings,
];

const shouldPreloadRoutes = () => {
  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!conn) return true;
  if (conn.saveData) return false;
  return !/(^|-)2g$/.test(conn.effectiveType || '');
};

class App extends Component {
  componentDidMount() {
    if (!shouldPreloadRoutes()) return;
    const preload = () => lazyRoutes.forEach(c => c.preload && c.preload().catch(() => {}));
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(preload, { timeout: 5000 });
    } else {
      setTimeout(preload, 2000);
    }
  }

  logout = () => {
    const { history, setUser } = this.props;
    setUser(null);
    history.push('/login');
  }

  isAuthenticated = () => {
    const { auth: { user } } = this.props;
    if (!user) return false;
    if (!user.expiresAt) return false;
    return new Date().getTime() < user.expiresAt;
  }

  render() {
    const { location } = this.props;
    return (
      <div>
        {location.pathname !== '/login'
          && location.pathname !== '/reset-password'
          && location.pathname !== '/auth/callback'
          && this.isAuthenticated()
          && <Nav logout={this.logout} />}
        <AnonymousRoute
          exact
          path='/login'
          component={AsyncLogin}
        />
        <AnonymousRoute
          exact
          path='/reset-password'
          component={AsyncResetPassword}
        />
        <AnonymousRoute
          exact
          path='/auth/callback'
          component={AsyncAuthCallback}
        />
        <Route
          exact
          path='/'
          component={AsyncDashboard}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/orders'
          component={AsyncOrders}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/ratings'
          component={AsyncRatings}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/wait-time'
          component={AsyncWaitTime}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/menu'
          component={AsyncMenu}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/menu/bulk-upload'
          component={AsyncBulkUpload}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/menu/bulk-upload/history'
          component={AsyncBulkUploadHistory}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/menu/bulk-upload/history/:uuid'
          component={AsyncBulkUploadDetail}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/menu/:uuid'
          component={AsyncSingleMenuItem}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/menu/create-category'
          component={AsyncCreateMenuCategory}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/menu/edit-category/:uuid'
          component={AsyncEditCategory}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/menu/create/:uuid'
          component={AsyncCreateMenuItem}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/menu/edit/:uuid'
          component={AsyncEditMenuItem}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/menu/create-modifier-group/:id'
          component={AsyncCreateModifierGroup}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/menu/create-modifier/:id'
          component={AsyncCreateModifier}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/menu/edit-modifier-group/:id'
          component={AsyncEditModifierGroup}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/menu/edit-modifier/:id'
          component={AsyncEditModifier}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/partners'
          component={AsyncPartners}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/partners/edit/:id'
          component={AsyncEditPartner}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/partners/create'
          component={AsyncCreatePartner}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/settings/update'
          component={AsyncUpdateSettings}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/service-availability/update'
          component={AsyncUpdateAvailability}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <Route
          exact
          path='/guide'
          component={AsyncGuide}
          redirectTo='/login'
          authenticated={this.isAuthenticated()}
        />
        <div className='spacer' />
        {location.pathname !== '/login'
          && location.pathname !== '/reset-password'
          && <Navigation />}
      </div>
    )
  }
}

const mapStateToProps = state => ({
  auth: state.auth,
  shop: state.app.shop,
});

const mapDisptachToProps = dispatch => ({
  setUser: user => dispatch(setCurrentUser(user)),
});

export default connect(mapStateToProps, mapDisptachToProps)(withRouter(App));